import { useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { getJson, postJson } from '../api/client.ts';
import { PublicShell } from '../components/layout.tsx';
import { Button, ErrorNote, Notice, Spinner, Textarea } from '../components/ui.tsx';
import { fmtDateTime } from '../lib/format.ts';

/**
 * Antwort aus der Behoben-Mail (F28) – ohne Anmeldung. Der Link öffnet nur
 * diese Seite (GET); erst der Knopf ändert den Zustand, weil Mail-Scanner und
 * Safe Links jeden Link vorab aufrufen.
 */
export function ConfirmPage() {
  const { token } = useParams({ from: '/antwort/$token' });
  const preselect = new URLSearchParams(window.location.search).get('aktion');
  const info = useQuery({
    queryKey: ['confirm', token],
    queryFn: () => getJson<{ number: string; title: string; machine: string | null; resolvedAt: string | null; resolutionText: string | null }>(`/api/confirm/${token}`),
    retry: false,
    // Nach der Antwort ist der Link verbraucht: Ein neuer Abruf könnte nur noch 410 liefern.
    // Beim Zurückkehren kommt deshalb nur ein gescheiterter erster Abruf noch einmal.
    refetchOnWindowFocus: (query) => query.state.data === undefined,
  });
  const [comment, setComment] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const send = async (action: 'running' | 'not_running') => {
    setError(null);
    try {
      await postJson('/api/confirm', { token, action, comment: comment || null });
      setDone(action === 'running' ? 'Danke! Das Ticket ist abgeschlossen.' : 'Danke! Die Instandhaltung schaut sich die Maschine erneut an.');
    } catch (err) {
      setError(err);
    }
  };

  return (
    <PublicShell>
      <div className="ad-card space-y-4">
        {done ? (
          <Notice tone="ok">{done}</Notice>
        ) : info.isPending ? (
          <Spinner />
        ) : info.isLoadingError ? (
          <ErrorNote error={info.error} />
        ) : (
          <>
            <p className="ad-label mb-0">{info.data.number}</p>
            <h1 className="text-xl normal-case">{info.data.title}</h1>
            {info.data.machine ? <p>Maschine: {info.data.machine}</p> : null}
            <p className="text-sm text-[var(--text-muted)]">
              Behoben {fmtDateTime(info.data.resolvedAt)}
              {info.data.resolutionText ? `: ${info.data.resolutionText}` : ''}
            </p>
            <p className="font-semibold">Läuft die Maschine wieder?</p>
            <Textarea label="Hinweis (optional)" value={comment} onChange={setComment} rows={2} />
            {error ? <ErrorNote error={error} /> : null}
            <div className="flex flex-wrap gap-2">
              <Button size="lg" variant={preselect === 'laeuft-nicht' ? 'outline' : 'primary'} onClick={() => void send('running')}>
                Läuft wieder
              </Button>
              <Button size="lg" variant={preselect === 'laeuft-nicht' ? 'primary' : 'outline'} onClick={() => void send('not_running')}>
                Läuft nicht
              </Button>
            </div>
          </>
        )}
      </div>
    </PublicShell>
  );
}
